import React from "react";
import { CalendarClock, Paperclip, Trash2 } from "lucide-react";
import { formatDateDDMMYYYY } from "../../../../shared/helpers/date";
import {
  getAppointmentStatusLabel,
  getAppointmentStatusColor,
  canDeleteAppointment,
} from "src/entities/appointment";
import { buildReschedulingLabel } from "src/entities/schedule";
import { IconButton } from "../../../../shared/ui/icons/IconButton/IconButton";
import { TechnicianFeedbackBadge } from "../../../../shared/ui/feedback/TechnicianFeedbackBadge";
import { BadgeCell } from "./BadgeCell";
import type { AppointmentListLayoutProps } from "../model/appointmentList.types";
import {
  canReschedule,
  durationTripletTitle,
  formatActualRange,
  formatDurationTriplet,
  formatTime,
  isShowActualDuration,
  isShowActualTime,
} from "../model/appointmentRow";

/** Row actions stay out of the way until the row is hovered — except on narrow screens, where
 *  there is no hover to give. */
const ACTION_CLASS =
  "opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 max-[1023px]:opacity-100";

export const AppointmentsTable: React.FC<AppointmentListLayoutProps> = ({
  appointments,
  onEdit,
  onReschedule,
  onDelete,
}) => {
  const showActualDuration = appointments.some(isShowActualDuration);
  const showActualTime = appointments.some(isShowActualTime);

  return (
    <table className="w-full text-sm font-['Roboto']">
      <thead>
        <tr className="border-b-2 border-gray-200 text-[#004494] text-left">
          <th className="px-3 max-[1535px]:px-2 py-3 pl-6 max-[1535px]:pl-4 font-medium">Termin</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Datum</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Uhrzeit</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Techniker</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Gewerk</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Anhänge</th>
          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Soll-Dauer</th>
          {showActualDuration && (
            <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Ist-Dauer</th>
          )}
          {showActualTime && (
            <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Beginn / Ende (ist)</th>
          )}

          <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Status</th>
          {/* Actions. Headerless, but the column still has to be declared so the widths line up. */}
          <th className="px-3 max-[1535px]:px-2 py-3 pr-6 max-[1535px]:pr-4 w-px" />
        </tr>
      </thead>
      <tbody>
        {appointments.map((a) => (
          <tr
            key={a.id}
            onClick={onEdit ? () => onEdit(a.id) : undefined}
            className={`group border-b border-gray-100 last:border-0 transition-colors duration-150 ${
              onEdit ? "cursor-pointer hover:bg-[#F6F8FC]" : ""
            }`}
          >
            <td className="px-3 max-[1535px]:px-2 py-4 pl-6 max-[1535px]:pl-4 text-gray-500 transition-colors duration-150">
              {a.sequenceNumber}.
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150">
              {formatDateDDMMYYYY(a.schedule.plannedWorkStartsAt)}
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150">
              {formatTime(a.schedule.plannedWorkStartsAt)}
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-normal break-words">
              {a.technician?.name ?? "—"}
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-normal break-words">
              {a.workOrder?.external?.gewerkeText ?? "—"}
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap">
              <span className="inline-flex items-center gap-2">
                {a.attachments?.length ? (
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <Paperclip className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span className="text-xs">{a.attachments.length}</span>
                  </span>
                ) : null}
                {/* The worker's comment and files, kept apart from the office's paperclip. The
                    comment itself is the tooltip — enough to decide whether to open the row. */}
                <TechnicianFeedbackBadge
                  tone="muted"
                  hasComment={Boolean(a.technicianComment?.trim())}
                  attachmentCount={a.technicianAttachments?.length ?? 0}
                  title={a.technicianComment?.trim() || undefined}
                />
              </span>
            </td>
            <td
              className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap"
              title={durationTripletTitle(
                a.schedule.plannedTravelMinutes,
                a.schedule.plannedWorkMinutes,
                a.schedule.plannedDepartureMinutes,
              )}
            >
              {formatDurationTriplet(
                a.schedule.plannedTravelMinutes,
                a.schedule.plannedWorkMinutes,
                a.schedule.plannedDepartureMinutes,
              )}
            </td>
            {showActualDuration && (
              <td
                className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap"
                title={durationTripletTitle(
                  a.schedule.actualTravelMinutes,
                  a.schedule.actualWorkMinutes,
                  a.schedule.actualDepartureMinutes,
                )}
              >
                {isShowActualDuration(a)
                  ? formatDurationTriplet(
                      a.schedule.actualTravelMinutes,
                      a.schedule.actualWorkMinutes,
                      a.schedule.actualDepartureMinutes,
                    )
                  : "—"}
              </td>
            )}
            {showActualTime && (
              <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap">
                {isShowActualTime(a) ? formatActualRange(a) : "—"}
              </td>
            )}
            <td className="px-3 max-[1535px]:px-2 py-4">
              <div className="flex flex-col gap-1 items-start">
                <BadgeCell
                  color={getAppointmentStatusColor(a.status)}
                  label={getAppointmentStatusLabel(a.status)}
                />

                {a.schedule.needsRescheduling && (
                  <BadgeCell
                    color="#ef4444"
                    label={buildReschedulingLabel(
                      a.schedule.reschedulingCause,
                      a.schedule.reschedulingReason,
                    )}
                  />
                )}
              </div>
            </td>
            <td className="px-3 max-[1535px]:px-2 py-4 pr-6 max-[1535px]:pr-4 w-px">
              <div className="flex items-center justify-end gap-1">
                {canReschedule(a) && (
                  <IconButton
                    variant="primary"
                    icon={<CalendarClock size={16} />}
                    aria-label="Auf Kalender verschieben"
                    title="Auf Kalender verschieben"
                    // The row itself opens the edit modal; this goes to the board instead.
                    onClick={() => onReschedule(a)}
                    className={ACTION_CLASS}
                  />
                )}
                {/* Absent for work that is done or already begun — the API refuses those with
                    409, so the button would only ever produce an error. */}
                {canDeleteAppointment(a) && (
                  <IconButton
                    icon={<Trash2 size={16} />}
                    aria-label="Termin löschen"
                    title="Termin löschen"
                    onClick={() => onDelete(a)}
                    className={ACTION_CLASS}
                  />
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
